import type { ReactElement } from 'react';

interface Testimonial {
  id: number;
  content: string;
  author: string;
  authorLink?: string | null;
}

interface TestimonialsProps {
  rating: string;
  totalReviews: number;
  title: string;
  testimonials: Testimonial[];
}

export function TestimonialsSection({
  rating,
  totalReviews,
  title,
  testimonials,
}: TestimonialsProps): ReactElement {
  const renderStars = () => {
    return (
      <div className="flex items-center justify-center gap-1 mb-4">
        {[...Array(5)].map((_, index) => (
          <span key={index} className="text-black text-lg">
            ★
          </span>
        ))}
        <span className="text-black text-sm ml-2">
          ({totalReviews} Google reviews)
        </span>
      </div>
    );
  };

  return (
    <section className="bg-gray-50 py-16">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Rating and Title */}
        <div className="text-center mb-12">
          <div className="text-black text-lg font-medium mb-2">
            {rating} {renderStars()}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-black">
            {title}
          </h1>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <div key={testimonial.id} className="bg-white">
              {/* Quote Icon */}
              <div className="text-6xl text-black font-bold mb-4 leading-none">
                "
              </div>
              
              {/* Testimonial Content */}
              <blockquote className="text-gray-700 text-base leading-relaxed mb-6">
                {testimonial.content}
              </blockquote>
              
              {/* Author */}
              <cite className="not-italic">
                {testimonial.authorLink ? (
                  <a
                    href={testimonial.authorLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 font-semibold text-sm uppercase tracking-wide hover:underline"
                  >
                    {testimonial.author}
                  </a>
                ) : (
                  <span className="text-red-600 font-semibold text-sm uppercase tracking-wide">
                    {testimonial.author}
                  </span>
                )}
              </cite>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}